import { useQuery } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { Link, NavLink, Outlet, useLocation } from 'react-router'

import { useAuth } from './AuthContext'
import { useSelectedHospital } from './SelectedHospitalContext'
import { TableFilterProvider, useTableFilter } from './TableFilterContext'
import { HospitalPicker } from './components/HospitalPicker'
import {
  HOSPITAL_OPTIONS_KEY,
  fetchHospitalOptions,
  fetchMyHospital,
  hospitalMeKey,
} from './api/hospitals'
import { PROFILE_KEY, fetchAdminProfile, initialsFor } from './api/profile'
import {
  isAdminRole,
  navForRole,
  roleLabelFor,
  pathFor,
  sectionLabelFor,
  type AdminRole,
} from './nav'
import { Icon } from '../components/Icon'

/**
 * The admin's chrome (M23-T02, ADR-0049): a 256px sidebar, a top bar with a
 * breadcrumb, and the content column. Replaces `AdminLayout`, whose horizontal
 * bar could not hold ten items at 1440 — a limit its own source comment
 * conceded.
 *
 * Everything role-dependent comes from `./nav`, not from conditionals here.
 *
 * ## Below `lg` (M23-T15)
 *
 * **Every frame in the file is 1440 and there is no narrow layout anywhere**,
 * so this is a decision the design does not make. It is written down in the
 * milestone README as well as here, because a choice that lives only in class
 * names is a choice nobody can disagree with later.
 *
 * The sidebar becomes a **drawer behind a menu button in the top bar**, over a
 * scrim, and the content column reclaims its 256px. The drawer is modal — focus
 * moves in, `Escape` closes it, focus returns to the button, and it closes on
 * navigation, because a nav drawer that stays open over the page you just asked
 * for is a nav drawer you have to dismiss twice.
 *
 * There is no `matchMedia` here on purpose. The menu button is `lg:hidden`, so
 * `open` can only be set below `lg`; at `lg` and up the sidebar is static and
 * the scrim is hidden, and the drawer state is inert rather than conditional.
 *
 * ## The collapsed rail (M24-T06, closes M23 gap A6)
 *
 * At `lg` and up the sidebar collapses to a **72px rail**: icons keep their
 * labels in `sr-only` and get them back as a tooltip on hover and focus, the
 * group headings go quiet, the MANAGING block becomes the hospital's initial,
 * and the content column's margin follows. The design is the milestone
 * README's, not a frame's — the frames draw the toggle and never draw what it
 * collapses to, which is why M23 correctly declined to build it (gap A6).
 *
 * `collapsed` follows the same no-`matchMedia` rule: every class it switches is
 * `lg:`-prefixed, so below `lg` the state is inert and the drawer above is
 * untouched. The one thing NOT expressible in a class name is the drawer's
 * focus query, which has to skip the `lg`-only toggle by hand.
 */

/**
 * The rail's collapsed/expanded choice (M24-T06).
 *
 * `localStorage`, never the server and never shared: one admin collapsing their
 * sidebar is not a decision about anyone else's. Both the read and the write
 * are wrapped — a browser with site data blocked THROWS on access, and the
 * right outcome there is an expanded sidebar, not a blank admin.
 */
const SIDEBAR_COLLAPSED_KEY = 'admin.sidebar.collapsed'

function readSidebarCollapsed(): boolean {
  try {
    return window.localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true'
  } catch {
    return false
  }
}

function writeSidebarCollapsed(next: boolean): void {
  try {
    window.localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(next))
  } catch {
    // Site data blocked. The choice then lasts this page load and no longer,
    // which is a better outcome than a shell that throws on a toggle.
  }
}

const navLinkClass =
  (collapsed: boolean) =>
  ({ isActive }: { isActive: boolean }): string =>
    [
      'group relative flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm',
      'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
      // `lg:` only — below it this is the drawer, which is always full width.
      collapsed ? 'lg:justify-center lg:px-0 lg:py-2.5' : '',
      isActive
        ? // font-semibold is the non-colour signal M17-T14 required: the active
          // tint sits close to the page ground, so colour alone would not carry
          // it for a sighted user scanning the list. The inactive weight is
          // medium, so nothing reflows on selection.
          'bg-accent-bg font-semibold text-accent-ink'
        : 'font-medium text-ink-2 hover:bg-bg-soft hover:text-ink-1',
    ].join(' ')

function GroupLabel({
  children,
  collapsed,
}: {
  children: string
  collapsed: boolean
}) {
  return (
    // `lg:sr-only`, not `lg:hidden`. The rail drops the heading visually, but a
    // screen-reader user still hears "PATIENTS" before the items under it, and
    // it costs no layout — `sr-only` takes up no space either.
    <p
      className={`px-2.5 pb-1 pt-4 text-xs font-medium uppercase tracking-wide text-ink-3 ${
        collapsed ? 'lg:sr-only' : ''
      }`}
    >
      {children}
    </p>
  )
}

/**
 * The label, given back on `:hover` AND on `:focus-visible`.
 *
 * Hover-only is unreachable by keyboard — the exact population that most needs
 * a label restored to a bare icon. `aria-hidden` because the link already
 * carries the same string in `sr-only`; without it every rail item would
 * announce its name twice.
 */
function RailTooltip({ children }: { children: string }) {
  return (
    <span
      aria-hidden="true"
      className="pointer-events-none absolute left-full top-1/2 z-50 ml-2 hidden -translate-y-1/2 whitespace-nowrap rounded-md bg-ink-1 px-2 py-1 text-xs font-medium text-bg-app opacity-0 shadow-pop transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100 lg:block"
    >
      {children}
    </span>
  )
}

/**
 * The signed-in admin.
 *
 * The frames draw a name, an email and initials. M23 shipped none of them and
 * recorded gap **A8**, because no endpoint exposed them. M24-T02 closes it:
 * `/api/v1/me` now reads `full_name` and `email` on demand — deliberately on
 * the route rather than on `AuthenticatedUser` or its 60s Redis cache, which
 * stay slim as a privacy boundary (ADR-0050).
 *
 * **The nameless card did not go away, and must not.** All twenty seeded
 * hospital admins have `full_name = NULL`, so on staging it is the common case,
 * not an edge: the card falls back to the role and a generic glyph, exactly as
 * before. Deriving a name from the email local-part would render
 * `ops@amtc.org` as "Ops" — the frame's own `admin@hotel.com` (M23 defect D12)
 * in a new costume.
 *
 * Identity is never a loading gate. A pending or failed `/me` renders the role
 * card; nobody waits on a name to see their queues.
 */
function UserCard({
  role,
  collapsed,
  onSignOut,
}: {
  role: string
  collapsed: boolean
  onSignOut: () => void
}) {
  const { token } = useAuth()
  const { data: profile } = useQuery({
    queryKey: PROFILE_KEY,
    queryFn: () => fetchAdminProfile(token),
    enabled: Boolean(token),
    staleTime: 5 * 60 * 1000,
  })
  const initials = initialsFor(profile?.full_name)
  const roleLabel = roleLabelFor(role)
  const [open, setOpen] = useState(false)
  const wrapRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false)
        triggerRef.current?.focus()
      }
    }
    const onClick = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('keydown', onKey)
    document.addEventListener('mousedown', onClick)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('mousedown', onClick)
    }
  }, [open])

  const glyph = initials ?? <Icon name="user" size={18} />

  return (
    <div ref={wrapRef} className="relative flex items-center gap-3 px-2.5 py-3">
      <span
        className={`grid h-10 w-10 shrink-0 place-items-center rounded-full bg-accent text-sm font-semibold text-accent-on ${
          collapsed ? 'lg:hidden' : ''
        }`}
        aria-hidden="true"
      >
        {glyph}
      </span>
      <span className={`min-w-0 flex-1 ${collapsed ? 'lg:hidden' : ''}`}>
        <span
          className={`block truncate text-sm font-semibold text-ink-1 ${
            profile?.full_name ? '' : 'capitalize'
          }`}
        >
          {profile?.full_name ?? roleLabel}
        </span>
        {/* The second line only appears when the first is a name — otherwise
            the card would read "hospital admin" over an email, which says the
            same thing twice and buries the role. */}
        {profile?.full_name && profile.email ? (
          <span className="block truncate text-xs text-ink-3">
            {profile.email}
          </span>
        ) : null}
      </span>
      {/* ONE trigger in both states. A second button carrying the same
          `aria-expanded` would be two controls for one menu, and `triggerRef`
          could only ever point at one of them. Collapsed, the button IS the
          glyph: the decorative glyph above hides and the chevron goes with it. */}
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Account menu"
        onClick={() => setOpen((v) => !v)}
        className={`grid h-8 w-8 shrink-0 place-items-center rounded-lg text-ink-3 hover:bg-bg-soft hover:text-ink-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent ${
          collapsed
            ? 'lg:mx-auto lg:h-10 lg:w-10 lg:rounded-full lg:bg-accent lg:text-sm lg:font-semibold lg:text-accent-on lg:hover:bg-accent lg:hover:text-accent-on'
            : ''
        }`}
      >
        <span className={collapsed ? 'lg:hidden' : ''}>
          <Icon name="chevd" size={16} />
        </span>
        {collapsed && (
          <span className="hidden lg:block" aria-hidden="true">
            {glyph}
          </span>
        )}
      </button>
      {open && (
        <div
          role="menu"
          className={`absolute bottom-full left-2.5 right-2.5 mb-1 rounded-[10px] border border-line-1 bg-bg-app p-1 shadow-pop ${
            // The rail is 72px wide; a menu squeezed into it would be
            // unreadable, so on the rail it escapes rightwards instead.
            collapsed ? 'lg:right-auto lg:w-44' : ''
          }`}
        >
          {/* "Your profile" sits ABOVE "Sign out" (M24-T08). A personal
              settings page does not belong in the sidebar, which lists queues
              of work — an admin would scan past it ten times a day. */}
          <Link
            to="/admin/profile"
            role="menuitem"
            onClick={() => setOpen(false)}
            className="block w-full rounded-lg px-2.5 py-2 text-left text-sm font-medium text-ink-1 hover:bg-bg-soft focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
          >
            Your profile
          </Link>
          <button
            type="button"
            role="menuitem"
            onClick={onSignOut}
            className="w-full rounded-lg px-2.5 py-2 text-left text-sm font-medium text-ink-1 hover:bg-bg-soft focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  )
}

/** The hospital in context, for the breadcrumb's first segment. */
function useHospitalInContext(role: AdminRole): string {
  const { token } = useAuth()
  const { hospitalId } = useSelectedHospital()
  const isAmtc = role === 'amtc_admin'
  // A coordinator administers no hospital: there is nothing to ask the server,
  // and both queries would answer it 403 (M36-T03).
  const isSupport = role === 'support'

  const { data: options } = useQuery({
    queryKey: HOSPITAL_OPTIONS_KEY,
    queryFn: () => fetchHospitalOptions(token),
    enabled: Boolean(token) && isAmtc,
    staleTime: 5 * 60 * 1000,
  })

  const { data: mine } = useQuery({
    queryKey: hospitalMeKey(null),
    queryFn: () => fetchMyHospital(token, null),
    enabled: Boolean(token) && !isAmtc && !isSupport,
  })

  if (isSupport) return 'AMTC'
  if (!isAmtc) return mine?.hospital.name ?? 'Your hospital'
  if (hospitalId === null) return 'All hospitals'
  return options?.find((h) => h.id === hospitalId)?.name ?? 'Selected hospital'
}

/**
 * The header's filter box. Every frame draws it and labels it "Search this
 * page"; there is no admin search endpoint on any of the twelve (M23 gap A5).
 * What ships filters the rows a table has already loaded, and is labelled for
 * what it does. Disabled whenever no table is mounted — a control that cannot
 * act should not look as though it can.
 */
function TopBarFilter() {
  const { query, setQuery, active, paginated, serverQueue } = useTableFilter()
  const { pathname } = useLocation()
  // A filter must not follow you to the next queue: leaving Appointments with
  // "mensah" typed and arriving at Diagnostics already filtered would read as
  // an empty queue.
  useEffect(() => setQuery(''), [pathname, setQuery])
  // The label follows the CAPABILITY, in three states rather than two
  // (M24-T05). "Search appointments" is a promise that the whole queue was
  // looked at; on the eight pages that filter loaded rows only, making that
  // promise is the defect this milestone exists to remove.
  const label =
    serverQueue !== null
      ? `Search ${serverQueue}`
      : paginated
        ? 'Filter the loaded rows'
        : 'Filter these rows'
  return (
    <div
      className={`flex min-w-0 max-w-[17rem] flex-1 items-center gap-2 rounded-full border border-line-1 py-1 pl-4 pr-1 sm:flex-none sm:max-w-none ${
        active ? '' : 'opacity-60'
      }`}
    >
      <label htmlFor="admin-filter" className="sr-only">
        {active ? label : 'Filter these rows (available on list pages)'}
      </label>
      <input
        id="admin-filter"
        type="search"
        disabled={!active}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={label}
        className="w-full min-w-0 bg-transparent text-sm text-ink-1 placeholder:text-ink-3 focus:outline-none sm:w-52"
      />
      <span
        className="grid h-8 w-8 place-items-center rounded-full bg-accent text-accent-on"
        aria-hidden="true"
      >
        <Icon name="search" size={15} />
      </span>
    </div>
  )
}

/**
 * The result line under the header (M24-T05).
 *
 * On a server-searched queue this is the sentence the whole milestone rests on:
 * it tells the admin the search covered the queue, so an empty result may
 * safely be read as "there is none" — the one place in the admin where that
 * conclusion is sound.
 *
 * `aria-live` sits on the count, never on the table: announcing a whole table
 * on every keystroke is how a screen-reader user loses the page.
 */
function SearchResultLine() {
  const { serverQueue, query, resultCount } = useTableFilter()
  const term = query.trim()
  const show = serverQueue !== null && term !== '' && resultCount !== null
  return (
    <p
      aria-live="polite"
      className={`px-4 text-sm text-ink-3 sm:px-8 lg:px-12 ${show ? 'pt-4' : 'sr-only'}`}
    >
      {show
        ? `${resultCount} ${resultCount === 1 ? 'result' : 'results'} for “${term}” — all of ${serverQueue} was searched.`
        : ''}
    </p>
  )
}

function AdminShellInner({ role }: { role: AdminRole }) {
  const { user, logout } = useAuth()
  const { pathname } = useLocation()
  const isAmtc = role === 'amtc_admin'
  const { hospitalId } = useSelectedHospital()
  const hospital = useHospitalInContext(role)
  const section = sectionLabelFor(pathname, role)
  // An amtc_admin who has picked nothing is managing all ten, which has no
  // initial to show. The rail needs to know, not just the name.
  const showsAllHospitals = isAmtc && hospitalId === null

  const [navOpen, setNavOpen] = useState(false)
  // Read once, lazily, on mount. Expanded is both the default and the fallback.
  const [collapsed, setCollapsed] = useState(readSidebarCollapsed)
  const menuRef = useRef<HTMLButtonElement>(null)
  const sidebarRef = useRef<HTMLElement>(null)

  const toggleCollapsed = () => {
    const next = !collapsed
    setCollapsed(next)
    writeSidebarCollapsed(next)
  }

  useEffect(() => {
    if (!navOpen) return
    // Focus the first thing in the drawer, not the drawer itself: a keyboard
    // user who opened a menu wants to be in the menu.
    sidebarRef.current
      ?.querySelector<HTMLElement>(
        // `[data-lg-only]` skips the rail toggle, which is `hidden lg:grid` and
        // so does not exist at the widths this drawer serves. `focus()` on a
        // `display: none` element is a silent no-op, so without the exclusion
        // the drawer would open with focus still on `body`.
        'a[href], button:not([disabled]):not([data-lg-only]), select:not([disabled])',
      )
      ?.focus()
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      setNavOpen(false)
      menuRef.current?.focus()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [navOpen])

  return (
    <div className="flex min-h-screen bg-bg-app font-sans text-ink-1">
      {/* The scrim. Below `lg` only — at `lg` the sidebar is not covering
          anything, so there is nothing to dismiss. */}
      {navOpen && (
        <div
          role="presentation"
          onClick={() => setNavOpen(false)}
          className="fixed inset-0 z-40 bg-black/40 lg:hidden"
        />
      )}

      {/* `invisible`, not just `-translate-x-full`. A translated element is
          still in the tab order: at 390 a keyboard user would tab through ten
          nav links they cannot see before reaching the page. `visibility:
          hidden` takes it out of the tab order, and `lg:visible` puts it back
          on desktop — so the behaviour follows the breakpoint with no
          `matchMedia` and no second source of truth. Caught by walking the tab
          order at 390, not by a test. */}
      {/* AN `aside`, NOT A `div` (M25-T02, finding F1).
          Screen readers navigate by landmarks. The only landmark in here used
          to be the `<nav>`, which wraps the links and nothing else — so the
          wordmark, the MANAGING block and the user card sat outside every
          region and were announced as content belonging to nothing. `region`
          failed on all twelve admin routes, in both sidebar states.

          One element fixes all four nodes at once because they are all already
          inside this container. Dates from M23-T02's shell.

          THE JSDOM AXE TESTS CANNOT SEE THIS, and adding another will not
          change that: `region` is judged against a whole page and does not fire
          on a rendered fragment, which is exactly why a real browser walk found
          it and eight milestones of green tests did not. The guard is therefore
          structural — a test asserting these nodes are inside a named
          complementary landmark, which jsdom CAN decide. */}
      <aside
        ref={sidebarRef}
        aria-label="Admin sidebar"
        className={`fixed inset-y-0 left-0 z-50 flex w-64 flex-col border-r border-line-1 bg-bg-app transition-transform lg:visible lg:translate-x-0 ${
          collapsed ? 'lg:w-[72px]' : ''
        } ${
          navOpen
            ? 'visible translate-x-0 shadow-pop'
            : 'invisible -translate-x-full'
        }`}
      >
        {/* The frames draw the toggle beside the wordmark and never draw what
            it collapses to. M23 declined to invent that state and recorded gap
            A6; the M24 README is now the design, written deliberately rather
            than improvised in a PR (ADR-0038, ADR-0050). */}
        <div
          className={`flex items-center justify-between gap-2 px-4 py-4 ${
            collapsed ? 'lg:justify-center' : ''
          }`}
        >
          <span
            className={`font-display text-lg font-semibold text-ink-1 ${
              collapsed ? 'lg:hidden' : ''
            }`}
          >
            Medcor
          </span>
          {/* `hidden lg:grid`. Below `lg` the T15 drawer already owns the
              sidebar's behaviour, and two controls for one thing is the bug,
              not the feature. */}
          <button
            type="button"
            data-lg-only=""
            onClick={toggleCollapsed}
            aria-expanded={!collapsed}
            aria-controls="admin-sidebar"
            aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'}
            className="hidden h-10 w-10 shrink-0 place-items-center rounded-lg text-ink-3 hover:bg-bg-soft hover:text-ink-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent lg:grid"
          >
            <Icon name={collapsed ? 'chevr' : 'chevl'} size={16} />
          </button>
        </div>
        <hr className="mx-4 border-line-1" />

        {/* A coordinator manages no hospital, so the block would only say
            something untrue (M36-T03). */}
        {role !== 'support' && (
          <div className={`px-4 pb-1 pt-4 ${collapsed ? 'lg:px-3' : ''}`}>
            <div className={collapsed ? 'lg:hidden' : ''}>
              <p className="pb-1.5 text-xs font-medium uppercase tracking-wide text-ink-3">
                Managing
              </p>
              {isAmtc ? (
                <HospitalPicker />
              ) : (
                // The block stays for both roles — that is what makes the two
                // read as the same product — but a hospital admin is never
                // offered a choice they do not have (ADR-0049 §4).
                <p className="truncate text-sm font-medium text-ink-1">
                  {hospital}
                </p>
              )}
            </div>
            {/* Collapsed: the initial in a bordered square, and NEVER a truncated
              `<select>` — a picker cannot be operated at 72px, and one squeezed
              to fit would read as a control that works. "All hospitals" has no
              initial, so that case gets the globe. Either way the square says
              something true, and the sentence beside it says it in full for a
              screen reader. */}
            {collapsed && (
              <div className="hidden lg:block">
                <span
                  aria-hidden="true"
                  className="mx-auto grid h-9 w-9 place-items-center rounded-[8px] border border-line-2 text-sm font-semibold text-ink-1"
                >
                  {showsAllHospitals ? (
                    <Icon name="globe" size={16} />
                  ) : (
                    hospital.trim().charAt(0).toUpperCase()
                  )}
                </span>
                <span className="sr-only">Managing {hospital}</span>
              </div>
            )}
          </div>
        )}

        <nav
          id="admin-sidebar"
          aria-label="Admin sections"
          className={`flex-1 overflow-y-auto px-4 pb-4 pt-1 ${
            collapsed ? 'lg:px-3' : ''
          }`}
        >
          {navForRole(role).map((sec) => (
            <div
              key={sec.group ?? 'primary'}
              // The heading is `sr-only` on the rail and so takes its spacing
              // with it. Without this the five groups run together as one
              // undifferentiated stack of ten icons.
              className={collapsed && sec.group !== null ? 'lg:mt-3' : ''}
            >
              {sec.group !== null && (
                <GroupLabel collapsed={collapsed}>{sec.group}</GroupLabel>
              )}
              {sec.items.map((item) => (
                <NavLink
                  key={item.label}
                  to={pathFor(item, role)}
                  end={item.end ?? false}
                  // Closing belongs to the tap, not to an effect watching the
                  // pathname: tapping a link in the drawer has already done the
                  // one thing the drawer is for, and a drawer you dismiss twice
                  // is a bug. (Doing it in an effect also trips the React
                  // Compiler's set-state-in-effect rule, and it is right to.)
                  onClick={() => setNavOpen(false)}
                  className={navLinkClass(collapsed)}
                >
                  <Icon name={item.icon} size={16} />
                  {/* `sr-only`, NEVER `hidden`: `display: none` would strip the
                      link's accessible name and leave a screen-reader user ten
                      unlabelled icons. */}
                  <span className={collapsed ? 'lg:sr-only' : ''}>
                    {item.label}
                  </span>
                  {collapsed && <RailTooltip>{item.label}</RailTooltip>}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        <hr className="mx-4 border-line-1" />
        <div className="px-1.5 pb-1">
          <UserCard
            role={user?.role ?? ''}
            collapsed={collapsed}
            onSignOut={logout}
          />
        </div>
      </aside>

      {/* ADR-0044's ramp, not a second spacing system. M20-T01's trap is the
          other half of it: full-bleed chrome STRETCHES content, so the column
          takes the ramp and the pages inside it keep their own measures. */}
      {/* `min-w-0` is load-bearing, not tidying. A flex item defaults to
          `min-width: auto`, so this column refuses to shrink below the
          min-content width of the widest thing inside it — and then the PAGE
          scrolls sideways instead of the table doing it. Measured at 390:
          seven of the twelve routes overflowed without this, up to 659px of
          document in a 390px viewport, with every suite green. */}
      <div
        className={`flex min-h-screen min-w-0 flex-1 flex-col ${
          collapsed ? 'lg:ml-[72px]' : 'lg:ml-64'
        }`}
      >
        <header className="flex h-18 shrink-0 items-center justify-between gap-3 border-b border-line-1 px-4 sm:gap-6 sm:px-8 lg:px-12">
          <button
            ref={menuRef}
            type="button"
            aria-label="Open navigation"
            aria-expanded={navOpen}
            aria-controls="admin-sidebar"
            onClick={() => setNavOpen(true)}
            className="-ml-1 grid h-10 w-10 shrink-0 place-items-center rounded-lg text-ink-2 hover:bg-bg-soft hover:text-ink-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent lg:hidden"
          >
            <Icon name="nav" size={18} />
          </button>

          <nav aria-label="Breadcrumb" className="min-w-0 flex-1">
            <ol className="flex min-w-0 items-center gap-2 text-sm">
              <li className="truncate font-medium text-ink-1">{hospital}</li>
              {section !== null && (
                <>
                  <li aria-hidden="true" className="text-ink-4">
                    <Icon name="chevr" size={14} />
                  </li>
                  <li aria-current="page" className="truncate text-ink-3">
                    {section}
                  </li>
                </>
              )}
            </ol>
          </nav>

          <TopBarFilter />
        </header>

        <SearchResultLine />

        <main className="min-w-0 flex-1 px-4 py-8 sm:px-8 lg:px-12">
          <Outlet />
        </main>
      </div>
    </div>
  )
}

/**
 * The provider has to sit above BOTH the header and the routed page: the filter
 * box is in the header and the rows it filters are on the page.
 */
export function AdminShell() {
  const { user } = useAuth()
  // `ProtectedRoute` has already signed out any role the admin does not admit.
  // This is the type-level half of that: no cast, and no fallback to
  // `hospital_admin` for a role nobody recognised (M36-T03).
  if (!user || !isAdminRole(user.role)) return null
  return (
    <TableFilterProvider>
      <AdminShellInner role={user.role} />
    </TableFilterProvider>
  )
}
